import { Component, Input, Output, AfterViewInit, ViewEncapsulation, EventEmitter } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, pipe } from 'rxjs';
import { map } from 'rxjs/operators';

import { AVATARS } from '@constants/miscellaneous';
import { IOperations } from '@interfaces/operations';
import { IUser } from '@interfaces/users';
import { Platform } from '@ionic/angular';
import { AuthService } from '@services/auth/auth.service';
import { FirebaseService } from '@services/firebase/firebase.service';
import { MovementsService } from '@services/movement/movement.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { ReadsService } from '@services/reads/reads.service';
import { StatusBarService } from '@services/status-bar/status-bar.service';
import { UserRestrictionsService } from '@services/user-restrictions/user-restrictions.service';
import { UserService } from '@services/user/user.service';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SideMenuComponent implements AfterViewInit {
  @Output() updateMiddleColumn = new EventEmitter<string>();
  @Output() updateSideMenuHidden = new EventEmitter<boolean>();
  public userInfo: IUser;
  public activeClass: string;
  public middleColumn: string;
  public middleColumnSelection: string;
  public hasMovementsNotifications: Observable<boolean>;
  private movementsToUpdate: string[] = [];
  public hasReadsNotifications: boolean;
  public profileImage: string;
  public isWEB: boolean;
  public isDarkMode: boolean;
  public isPro: boolean;
  hideSideMenu: boolean;
  hasDrivewealthAccess: boolean;

  constructor(
    private movementsService: MovementsService,
    private firebaseService: FirebaseService,
    private readsService: ReadsService,
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
    private platform: Platform,
    private statusBarService: StatusBarService,
    private navigationService: NavigationService,
    private userRestrictionsService: UserRestrictionsService,
  ) {
    this.isWEB = !this.platform.is('cordova');
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd && window.innerWidth >= 570) {
        this.setActiveTab(event.url);
      }
    });
  }

  ngAfterViewInit() {
    this.setActiveTab(this.router.url);
    this.hasMovementsNotifications = this.movementsService.currentUnreadDepositsSubSource.pipe(
      map(result => {
        if (result && result.length) {
          this.movementsToUpdate = result.map(deposit => deposit.id);
          return true;
        }
        return false;
      }),
    );
    Observable.combineLatest([
      this.authService.currentUserSource,
      this.readsService.getLastReadDate(),
      this.userService.currentSocialProfileSource,
      this.statusBarService.currentDarkModeSource,
    ]).subscribe(([userInfo, lastReadDate, ownSocialProfile, isDarkMode]) => {
      this.isDarkMode = isDarkMode;
      this.userInfo = userInfo;
      if (userInfo && lastReadDate) {
        this.isPro = this.userInfo.subscription === 'pro';
        this.hasDrivewealthAccess = userInfo.drivewealthAccess !== false || userInfo.drivewealthAccess === undefined;
        const dateTime = userInfo.lastReadDate || userInfo.createdAt;
        const lastUserReadDate = new Date(Math.max(dateTime.toDate(), 1603854000000));
        this.hasReadsNotifications = lastReadDate > lastUserReadDate;
        const animalImageFromUID = userInfo.uid.split('').map(s => s.charCodeAt(0)).reduce((a, b) => a + b, 0) % 12;
        if (ownSocialProfile) {
          const animalImage = ownSocialProfile.animalImage === undefined ? animalImageFromUID : ownSocialProfile.animalImage;
          this.profileImage = ownSocialProfile.profileImage || AVATARS[animalImage];
        } else  {
          this.profileImage = AVATARS[animalImageFromUID];
        }
      }
    });
  }

  async goToDeposit() {
    const canBuyStocks = await this.userRestrictionsService
      .canOperate(this.userInfo.restrictedReason, IOperations.buyStocks);
    const canBuyPortfolio = await this.userRestrictionsService
      .canOperate(this.userInfo.restrictedReason, IOperations.buyPortfolio);
    const canOperate = canBuyStocks || canBuyPortfolio;

    if (!canOperate) { return this.userRestrictionsService.showRestrictedUserToast(); }

    this.navigationService.goToRoot(`${this.isWEB ? '/deposit-info' : '/deposit-amount'}`);
  }

  setActiveTab(url: string) {
    this.activeClass = this.getNewPage(url) || this.activeClass;

    let hideSideMenu = false;
    this.middleColumnSelection = undefined;

    if (url.includes('movements')) {
      this.middleColumn = 'movements';
      if (url.includes('movements/passive/')) {
        this.middleColumnSelection = url.split('movements/passive/')[1].split('?')[0];
      } else if (url.includes('movements/')) {
        this.middleColumnSelection = url.split('movements/')[1].split('?')[0];
      }
    } else if (url.includes('movement?')) {
      this.middleColumn = 'movements';
      this.middleColumnSelection = url.split('movementId=')[1].split('&')[0];
    } else if (url.includes('asset-details/')) {
      this.middleColumn = 'market';
      this.middleColumnSelection = url.split('asset-details/')[1].split('?')[0];
    } else if (url.includes('market')) {
      this.middleColumn = 'market';
      this.middleColumnSelection = undefined;
    } else if (url.includes('contribution-strategy')) {
      this.middleColumn = 'movements';
      this.middleColumnSelection = 'strategy';
    } else if (url.includes('profile') && !url.includes('settings') && url.split('/').length !== 3) {
      this.middleColumn = 'profile-settings';
    } else if (url.includes('profile') && url.includes('edit')) {
      this.middleColumn = 'profile-settings';
    } else if (url.includes('my-subscription') && !url.includes('deposit-amount')) {
      this.middleColumn = 'profile-settings';
      this.middleColumnSelection = 'subscription';
    } else if (url.includes('my-data')) {
      this.middleColumn = 'profile-settings';
      this.middleColumnSelection = 'data';
    } else if (url.includes('profile/edit')) {
      if (this.userInfo && (this.userInfo.drivewealthAccess === false || !this.userInfo.profileId)) {
        this.navigationService.goTo('settings', {}, true);
      }
      this.middleColumn = 'profile';
      this.middleColumnSelection = 'data';
    } else if (url.includes('bank-information') && !url.includes('source=withdrawal')) {
      this.middleColumn = 'profile-settings';
      this.middleColumnSelection = 'data';
    } else if (url.includes('contracts/')) {
      this.middleColumn = 'profile-settings';
      this.middleColumnSelection = 'investments';
    } else if (url.includes('contracts')) {
      this.middleColumn = 'profile-settings';
      this.middleColumnSelection = 'data';
    } else if (url.includes('investments')) {
      this.middleColumn = 'profile-settings';
      this.middleColumnSelection = 'investments';
    } else if (url.includes('invite-contacts')) {
      this.navigationService.goTo('referrals', {}, true);
    } else if (url.includes('referrals')) {
      this.middleColumn = 'profile-settings';
      this.middleColumnSelection = 'referrals';
    } else if (url.includes('settings') && !url.includes('profile')) {
      this.middleColumn = 'profile-settings';
      this.middleColumnSelection = 'settings';
    } else if (this.isOnBoardingPage(url)) {
      hideSideMenu = true;
    } else {
      this.middleColumn = undefined;
    }

    this.hideSideMenu = hideSideMenu;
    this.updateSideMenuHidden.emit(hideSideMenu);
    this.updateMiddleColumn.emit(this.middleColumn);
  }

  changeActiveClass(type: string) {
    if (type === 'movements') {
      const batch = this.firebaseService.firestore.batch();
      this.movementsToUpdate.forEach(id => {
        const collection = this.firebaseService.firestore.collection('deposits').doc(id);
        batch.update(collection, {read: true});
      });
      batch.commit();
    }
  }

  toggleDarkMode() {
    this.statusBarService.setAppDarkMode(this.isDarkMode ? 'light' : 'dark');
  }

  goToProfile() {
    this.navigationService.goToRoot(`profile`);
  }

  getNewPage(url: string) {
    if (url.includes('home')) {
      return 'home';
    }
    if (url.includes('movements')) {
      return 'movements';
    }
    if (url.includes('reads')) {
      return 'reads';
    }
    if (url.includes('discover')) {
      return 'discover';
    }
    if (url.includes('markets?favoritesOnly')) {
      return 'favorites';
    }
    if (url.includes('market')) {
      return 'market';
    }
    if (url.includes('profile')) {
      return 'profile';
    }
    return undefined;
  }

  isOnBoardingPage(url: string) {
    return url.includes('initial-questionnaire') || url.includes('extended-investment-form') || url.includes('drivewealth-agreements');
  }
}
